import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Trophy, Sparkles, Check, X, Mail, Phone } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { thumb, fallbackToFull } from "../lib/imageThumb";
import { useReglages } from "../lib/useReglages";

const TIERS = [
  { name: "Bronze",   amount: "CHF 300.–",     highlight: false },
  { name: "Argent",   amount: "CHF 500.–",     highlight: false },
  { name: "Or",       amount: "CHF 1'000.–",   highlight: false },
  { name: "Platine",  amount: "CHF 2'500.–",   highlight: true,  badge: "Contact direct" },
  { name: "Principal",amount: "dès CHF 5'000.–", highlight: true, badge: "Contact direct" },
];

const BENEFITS = [
  { label: "Nom sur le site internet",                                                   values: [1,1,1,1,1] },
  { label: "Mention dans les remerciements officiels",                                   values: [1,1,1,1,1] },
  { label: "Logo sur supports de communication (flyers, programmes)",                    values: [0,1,1,1,1] },
  { label: "1 story Instagram",                                                          values: [0,1,1,1,1] },
  { label: "Logo affiché dans l'école (mur sponsors)",                                   values: [0,0,1,1,1] },
  { label: "Mention lors des spectacles",                                                values: [0,0,1,1,1] },
  { label: "1 publication sur les réseaux sociaux",                                      values: [0,0,1,1,1] },
  { label: "Logo sur supports visibles lors des spectacles (backdrop / textile)",        values: [0,0,0,1,1] },
  { label: "Possibilité de présence lors d'événements",                                  values: [0,0,0,1,1] },
  { label: "Activité de team building",                                                  values: [0,0,0,1,1] },
  { label: "1 publication vidéo dédiée",                                                 values: [0,0,0,1,1] },
  { label: "Visibilité maximale + logo prioritaire sur tous les supports",               values: [0,0,0,0,1] },
  { label: "Mention officielle « Projet soutenu par [Nom de votre entreprise] »",        values: [0,0,0,0,1] },
  { label: "10 % de réduction pour les collaborateurs, 20 % pour leurs enfants",         values: [0,0,0,0,1] },
  { label: "Priorité sur les stands lors d'événements",                                  values: [0,0,0,0,1] },
];

export default function Sponsoring() {
  // Nombre d'élèves lu en direct dans la console (GET /api/reglages).
  const { eleves_actifs: nbEleves } = useReglages();
  useSEO({
    title: "Devenir sponsor de 1000 Arts — Investis dans une école qui monte",
    description: "Soutiens 1000 Arts, école de danse et arts de la scène à Cugy VD. Plus de 200 élèves, 1000+ spectateurs. Le sponsoring soutient l'école, ses spectacles et ses événements (costumes, régie son, intervenants externes, matériel pédagogique). 5 paliers de sponsoring (Bronze à Principal) avec visibilité locale ciblée.",
    canonical: "/sponsoring",
  });

  return (
    <div data-testid="page-sponsoring" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      {/* HERO */}
      <div className="eyebrow mb-3">Sponsoring</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight max-w-4xl">
        Devenir sponsor <br /><span style={{ color: "var(--primary)" }}>de 1000 Arts</span>
      </h1>
      <p className="mt-6 text-lg md:text-xl text-[var(--muted)] max-w-3xl leading-relaxed">
        Vous ne faites pas qu'un don, vous investissez dans une communauté dynamique et en croissance.
      </p>

      {/* QUI SOMMES-NOUS + KPI */}
      <section className="mt-20 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Qui sommes-nous&nbsp;?</h2>
          <p className="mt-4 text-[var(--text)] leading-relaxed">
            1000 Arts est une école de danse et d'arts de la scène située dans la région de Cugy (VD). Cours pour enfants, adolescents et adultes, mêlant hip-hop, comédie musicale, théâtre et performance scénique.
          </p>
          <p className="mt-4 text-[var(--text)] leading-relaxed">
            <strong>Notre mission :</strong> créer un espace artistique inspirant, accessible et formateur pour la nouvelle génération.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[
            { icon: Users,    v: `${nbEleves || 230}`,  l: "élèves actifs", testid: "sponsoring-nb-eleves" },
            { icon: Trophy,   v: "1000+", l: "spectateurs lors des spectacles" },
            { icon: Sparkles, v: "★",     l: "Forte présence régionale et sur les réseaux sociaux" },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="rounded-2xl p-5 flex items-center gap-5" style={{ background: "var(--primary-tint)" }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--primary)", color: "#fff" }}>
                  <Icon size={22} />
                </div>
                <div>
                  <div className="font-display text-3xl leading-none" style={{ color: "var(--primary)" }} data-testid={k.testid}>{k.v}</div>
                  <div className="text-sm text-[var(--muted)] mt-1">{k.l}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROJET */}
      <section className="mt-24 card-flat" style={{ background: "var(--bg-soft)", borderColor: "transparent", padding: 40 }}>
        <div className="eyebrow mb-3">Notre projet</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Soutenir 1000 ARTS, c'est faire grandir la scène</h2>
        <p className="mt-3 text-[var(--text)] max-w-3xl">
          Le sponsoring nous permet de faire vivre l'école au quotidien et de donner à nos élèves une <strong>vraie expérience artistique</strong>&nbsp;: costumes de spectacle, régie son et lumière, intervenants externes, matériel pédagogique, ateliers thématiques… Chaque contribution rend l'art plus accessible, plus qualitatif et plus inspirant pour les {nbEleves || 230} élèves qui grandissent chez nous.
        </p>
        <div className="mt-6 grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold mb-3">Ce que soutient le sponsoring</h3>
            <ul className="space-y-2 text-[var(--text)]">
              <li>• Costumes et décors pour les spectacles et le Gala annuel</li>
              <li>• Régie son, lumière et équipements techniques de scène</li>
              <li>• Intervenants externes (chorégraphes invités, coachs, artistes)</li>
              <li>• Matériel pédagogique et petits équipements de studio</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-3">Événements soutenus</h3>
            <ul className="space-y-2 text-[var(--text)]">
              <li>• Gala annuel (100+ élèves sur scène)</li>
              <li>• Spectacles de saison (Noël solidaire, Halloween)</li>
              <li>• Stages pendant les vacances scolaires</li>
              <li>• Portes ouvertes, auditions et projets artistiques ponctuels</li>
            </ul>
          </div>
        </div>
      </section>

      {/* POURQUOI DEVENIR SPONSOR */}
      <section className="mt-24">
        <div className="eyebrow mb-3">Bénéfices</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Pourquoi devenir sponsor&nbsp;?</h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {[
            "Associer votre image à un projet positif, culturel et éducatif",
            "Bénéficier d'une visibilité locale ciblée (familles, jeunes, communauté)",
            "Être présent lors d'événements artistiques et spectacles",
            "Soutenir activement la jeunesse et la culture régionale",
          ].map((t, i) => (
            <div key={i} className="rounded-2xl border border-[var(--border-soft)] p-5 flex items-start gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
                <Check size={14} />
              </div>
              <span className="text-[var(--text)]">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* OFFRES DE SPONSORING (tableau responsive) */}
      <section className="mt-24" data-testid="sponsoring-tiers">
        <div className="eyebrow mb-3">Nos offres</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Nos offres de sponsoring</h2>

        {/* Desktop : tableau */}
        <div className="hidden lg:block mt-10 overflow-x-auto rounded-2xl border border-[var(--border-soft)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[var(--border-soft)] bg-[var(--bg-soft)]">
                <th className="text-left p-4 font-medium">Avantage</th>
                {TIERS.map((t, i) => (
                  <th
                    key={i}
                    className="p-4 text-center"
                    style={t.highlight ? { background: "var(--primary-tint)", color: "var(--primary)" } : {}}
                  >
                    <div className="font-display text-lg">{t.name}</div>
                    <div className="text-xs font-normal opacity-80 mt-1">{t.amount}</div>
                    {t.badge && <div className="text-[10px] font-semibold uppercase tracking-wider mt-2 px-2 py-0.5 rounded-full inline-block" style={{ background: "var(--primary)", color: "#fff" }}>{t.badge}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BENEFITS.map((b, i) => (
                <tr key={i} className="border-b border-[var(--border-soft)] last:border-b-0">
                  <td className="p-4 text-[var(--text)]">{b.label}</td>
                  {b.values.map((v, j) => (
                    <td key={j} className="p-4 text-center" style={TIERS[j].highlight ? { background: "rgba(0,198,206,0.05)" } : {}}>
                      {v ? <Check size={18} className="inline" style={{ color: "var(--primary)" }} /> : <X size={16} className="inline opacity-25" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile : cartes empilées */}
        <div className="lg:hidden mt-10 space-y-4">
          {TIERS.map((t, i) => (
            <div
              key={i}
              className="rounded-2xl border p-5"
              style={{ borderColor: t.highlight ? "var(--primary)" : "var(--border-soft)", background: t.highlight ? "var(--primary-tint)" : "#fff" }}
            >
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="font-display text-2xl">{t.name}</div>
                  {t.badge && <div className="text-[10px] font-semibold uppercase tracking-wider mt-1 px-2 py-0.5 rounded-full inline-block" style={{ background: "var(--primary)", color: "#fff" }}>{t.badge}</div>}
                </div>
                <div className="font-display text-xl" style={{ color: "var(--primary)" }}>{t.amount}</div>
              </div>
              <ul className="space-y-2 text-sm">
                {BENEFITS.filter((b) => b.values[i]).map((b, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <Check size={14} className="shrink-0 mt-1" style={{ color: "var(--primary)" }} />
                    <span>{b.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* IMPACT */}
      <section className="mt-24 rounded-3xl p-10 md:p-14" style={{ background: "var(--primary)", color: "#fff" }}>
        <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Notre impact</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Plus qu'une école, un acteur culturel régional</h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-white/90">
          <div>+ 230 élèves et leurs familles</div>
          <div>Une communauté locale engagée</div>
          <div>Plus de 1000 spectateurs lors des spectacles</div>
          <div>Une visibilité digitale en constante évolution</div>
        </div>
      </section>

      {/* REJOINDRE L'AVENTURE */}
      <section className="mt-24" data-testid="sponsoring-join">
        <div className="eyebrow mb-3">Rejoignez l'aventure</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Deux façons de nous soutenir</h2>

        <div className="mt-10 grid md:grid-cols-2 gap-8">
          {/* Soutien direct + QR codes */}
          <div className="rounded-3xl border-2 border-[var(--primary)] p-8">
            <div className="font-display text-2xl mb-2">Soutien direct</div>
            <p className="text-sm text-[var(--muted)] mb-6">Montant libre ou pack sponsor (Bronze à Or). Deux moyens de paiement — scanne l'un des QR codes :</p>
            <div className="grid grid-cols-2 gap-6">
              <div className="rounded-xl border border-[var(--border-soft)] p-4 text-center bg-white" data-testid="qr-twint">
                <img loading="lazy" decoding="async"
                  src={thumb("/img/536c019846.webp")} onError={fallbackToFull("/img/536c019846.webp")}
                  alt="QR code Twint — 1000 Arts"
                  className="w-full h-auto rounded-lg"
                />
                <div className="text-sm font-medium mt-3">Twint</div>
              </div>
              <div className="rounded-xl border border-[var(--border-soft)] p-4 text-center bg-white" data-testid="qr-versement">
                <img loading="lazy" decoding="async"
                  src={thumb("/img/e7a16daa2d.webp")} onError={fallbackToFull("/img/e7a16daa2d.webp")}
                  alt="QR code de versement bancaire — 1000 Arts"
                  className="w-full h-auto rounded-lg"
                />
                <div className="text-sm font-medium mt-3">Versement</div>
              </div>
            </div>
          </div>

          {/* Contact Platine / Principal */}
          <div className="rounded-3xl border-2 border-[var(--primary)] p-8" style={{ background: "var(--primary-tint)" }}>
            <div className="font-display text-2xl mb-2">Sponsor Platine ou Principal</div>
            <p className="text-sm text-[var(--muted)] mb-6">Pour un partenariat premium, contactons-nous directement :</p>
            <div className="space-y-3">
              <a href="mailto:info1000arts@gmail.com" data-testid="sponsoring-email" className="flex items-center gap-3 text-[var(--text)] hover:text-[var(--primary)]">
                <Mail size={18} style={{ color: "var(--primary)" }} /> info1000arts@gmail.com
              </a>
              <a href="tel:+41799504031" data-testid="sponsoring-phone" className="flex items-center gap-3 text-[var(--text)] hover:text-[var(--primary)]">
                <Phone size={18} style={{ color: "var(--primary)" }} /> 079 950 40 31
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final vers Contact */}
      <section className="mt-16 text-center">
        <Link to="/contact" data-testid="sponsoring-final-cta" className="btn-primary" style={{ padding: "14px 28px" }}>
          Contactez-nous <ArrowRight size={16} />
        </Link>
      </section>
      </div>
    </div>
  );
}
